<template>
	<div class="">
		<div class="flex items-center mb-3 px-8">
			<div class="h-2 rounded-lg overflow-clip bg-gray-2 w-full">
				<div class="h-full rounded-lg bg-green" :style="{ width: attendancePercentage }"></div>
			</div>
			<div class="text-gray-1 text-sm ml-3">{{attendancePercentage}}</div>
		</div>

		
		<div class="px-8">
			<m-search-pop-list
				v-if="canEdit"
				placeholder="Add user or ward"
				class="mb-3 "
				:searchList="filteredList"
				@input="(text) => searchText = text"
				@close="() => searchText = ''"
				@change="(item) => addAttendee(item)"
				>
			</m-search-pop-list>
		</div>

		<div class="mb-3">
		

			<template v-for="(group) in groupedAttendance">
				<div :key="group.group">
					<div class="flex items-center mb-2 px-8">
						<div class="flex-initial pr-3 text-gray-1 font-base font-bold">{{ UserUtils.getWardName(wards, group.group) }}</div>
						<div class="flex-1 overflow-clip bg-gray-2 rounded-lg h-1 my-auto">
							<div class="h-full rounded-lg bg-green" :style="{ width: groupAttendancePercent(group) }"></div>
						</div>
						<div class="flex-initial text-gray-1 text-sm ml-3 text-center">{{groupAttendancePercent(group)}}</div>
					</div>

					<m-slide-left-list-reveal 
						:readonly="!canEdit"
						@delete="() => removeUserFromList(attendee)"
						:hasEditBtn="false" 
						v-for="(attendee, n) in group.users || []" 
						:key="n">
						<div  class="checklistItem !px-4 mb-1 cursor-pointer px-8" 
							:class="[{ 'checked': attendee?.is_completed }]">
			
							<span :key="i" v-if="!getAttendeeImage(attendee)" :class="[{ '-ml-4': i != 0 }]"
								class="border border-gray rounded-full w-8 h-8 flex items-center justify-center bg-white">
								<church-icon class="!w-4" icon="person-fill" />
							</span>
			
							<img v-else class="w-12 h-12 rounded-full object-cover mr-3" :src="getAttendeeImage(attendee)"
								alt="">
			
							<div class="flex flex-1 flex-col justify-center text-left">
								<div>{{UserUtils.getFullName(attendee?.assign_to_user) || ''}}</div>
								<p class="mb-0 text-sm text-gray-1">{{attendee?.assign_position || ''}}</p>
							</div>
			
							<div class="checkMarker mr-0" @click="() => toggleAttendance(attendee)">
								<church-icon icon="check" />
							</div>
						</div>
					</m-slide-left-list-reveal>

				</div>
			</template>

		</div>

		<m-modal 
            :show="showDeleteConfirm " 
            :icon="'trash'"
            @close="() => showDeleteConfirm = false" 
            @continue="() => {}"
            >

            <div class="text-black text-center">
                <h1>Delete Meeting</h1>
                <p>Deleting this meeting will permanently remove the data, and this action cannot be undone.</p>
            </div>
        </m-modal>
	</div>
</template>

<script setup >
import MeetingApis from '@/apis/meetingApis'
import StringParser from '@/helpers/stringParser'
import UserUtils from '@/helpers/userUtils'
import store from '@/store'
import {defineProps, defineEmits, computed, ref, onBeforeMount} from 'vue'

/* =========== PROPS ========= */
const props = defineProps({
	meeting : {type : Object, default : () =>  {}},
	canEdit : {type : Boolean, default : () => false},
	userList : {type : Array, default : () => []},
	wards : {type : Array, default : () => []},
})

/* ============ EMITS ======= */
const emit = defineEmits(['update:meeting'])


/* =============== STATES ============ */
const searchText = ref('')
const showDeleteConfirm = ref(false)


/* =========== COMPUTED ======== */

/**
 * Current user
 */
const currentUser = computed(() => store.state.user)

/**
 * Check if the user is assigned to stake 
 */
const isStake = computed(() => UserUtils.isAssignedToStakeOrWard(currentUser.value?.user_assign_wards || []) == 'stake' ? true : false)

/**
 * Compute the attendance percentage
 */
const attendancePercentage = computed(() => {
	const attendance = props.meeting?.task_attendance || []
	const total = attendance.length
	const completed = attendance.filter(a => a.is_completed).length
	const percent =  total ? Math.round((completed / total) * 100) : 0

	return `${percent}%`
})

/**
 * Filter the list of users and wards
 */
const filteredList = computed(() => {
	console.log('searchText.value', searchText.value)

	if(!searchText.value) return []
	let  list = [...props.userList]
	let wards = [...props.wards]

	/**
	 * Remove wards that don't have users assigned to them
	 */
	wards = wards.filter(ward => {
		const wardUsers = props.userList.filter(u => u?.assign_ward?.includes(ward?.site_domain)) || []
		return wardUsers.length > 0
	})

	/* If not stake filter the list to only return users that are assigned to same ward as the currentuser */
	console.log('isStake.value', isStake.value)
	if(!isStake.value) {

		/* Check if current has role of admin then user has access to all ward users */

		const currentUserWardIDs = currentUser.value?.user_assign_wards?.map(ward => ward) || [];
		list = currentUser?.value?.user_info?.roles?.includes?.('administrator') ? list  :  list.filter(user => 
			user?.assign_ward?.some(wardID => currentUserWardIDs.includes(wardID))
		);

		/* Add user ward */
		const userWards = wards.filter(ward => currentUser?.value?.user_assign_wards?.includes(ward?.site_domain))
		list = [...list, ...userWards]
	}  else list = [...list, ...wards]

	console.log('filtered list', list)

	list = list.filter(item => {
		if(!searchText.value) return true
		return item?.display_name?.toLowerCase()?.includes(searchText.value?.toLowerCase()) || item?.header?.toLowerCase()?.includes(searchText.value?.toLowerCase())
	}).map(l => {
		let label = UserUtils.getFullName(l) || l?.site_domain || l?.header || ''
		if(l?.user_email) label += ` - (${l?.assign_position || l?.user_email || ''})`

		return {
			label : label,
			value : l?.id || l?.ID || l?.site_domain,
			type : l?.display_name ? 'user' : 'ward',
		}
	})

	/**
	 * Remove users that are already in the meeting
	 */
	list = list.filter(l => {
		const isUser = props.meeting?.task_attendance?.find?.(a => Number(a?.assign_to_user?.ID) == Number(l?.value))
		return !isUser
	})

	return list
})


/**
 * Grouped  user by wards or organizations
 */
const groupedAttendance = computed(() => {

	let attendance = props.meeting?.task_attendance || []
	attendance = attendance.map(a => {
		const user = props.userList.find(u => Number(u.ID) == Number(a.assign_to_user?.ID))

		return {
			...a,
			assign_wards : user?.assign_ward || [],
			assign_organizations : user?.assign_organization || [],
			assign_position : user?.assign_position || ''
		}
	})

	if(!isStake.value) {
		const orgs = attendance.map(a => a?.assign_organizations?.name)
		.filter((v, i, a) => a.indexOf(v) === i) // remove duplicates
		
		return orgs.map(org => {
			return {
				group : org,
				users : attendance.filter(a => a?.assign_organizations?.name == org)
			}
		})
	}

	/* Get all attendance user wards  */
	let wards = attendance.map(a => {
		return a?.assign_wards || []
	})
	.flat()
	.filter((v, i, a) => a.indexOf(v) === i) // remove duplicates	
	.filter(w => UserUtils.isAssignedToStakeOrWard([w]) == 'ward')// remove stake from the list


	console.log('wards group', attendance)
	

	/* Get all user assigned to wards */
	return wards.map(ward => {
		return {
			group : ward,
			users : attendance.filter(a => a?.assign_wards?.includes(ward))
		}
	})
	
})

/* ============= METHODS ============ */


// /**
//  * Get attendee's organization
//  * @param {Object} attendee
//  */
// const getOrganization = (attendee) => {
// 	return attendee?.select_organization?.map?.(o => o?.post_title)?.join?.(', ') || ''
// }

/**
 * Get attendee's image
 * @param {Object} attendee
 */	
const getAttendeeImage = (attendee) => {
	const image =  attendee?.assign_to_user?.user_profile || attendee?.assign_to_user?.user_avatar || null
	const [url] = StringParser.extractUrl({string : image}) || []
	return url || null
}

/**
 * Toggle attendance
 * @param {Object} attendee
 * 
 */
const toggleAttendance = (attendee) => {
	if(attendee?.is_completed) return; // already completed
	// check if the task is already completed
	let	 attendance = props.meeting?.task_attendance || []
	attendance = attendance.map(a => {
		if(a.task_id === attendee.task_id) {
			a.is_completed = true
		}
		return a
	})

	emit('update:meeting', {
		...props.meeting,
		task_attendance: attendance
	})
	
	MeetingApis.toggleAttendance({meeting_id : props.meeting?.id, task_id : attendee.task_id})
}

/**
 * Add attendee
 * @param {Object} item
 */
const addAttendee = async (item) => {
	if(!item) return
	// reset the search text
		searchText.value = ''

	const {value, type, label} = item

	let params = {
		type : 'individual',
		assign_to_user : value,
		meeting_id : props.meeting?.id,
		task_name : label || '',
		assign_to : ''
	}

	
	if(type === 'ward') {
		// add ward users to the meeting
		let wardUsers = props.userList.filter(u => u?.assign_ward?.includes(value))?.map(u => u?.ID) || []

		console.log(item, 'ward users', wardUsers)

		if(wardUsers?.length == 0) return; // prevent adding empty ward

		// remove users that are already in the meeting
		wardUsers = wardUsers.filter(u => {
			const isUser = props.meeting?.task_attendance?.find?.(a => Number(a?.assign_to_user?.ID) == Number(u))
			return !isUser
		})

		// Loop through the users and add them to the meeting
		let addedUsers = []
		for (let index = 0; index < wardUsers?.length; index++) {
			const user = wardUsers[index]
			params.assign_to_user = user

			await MeetingApis.addAttendance({params, callback : (data) => {
				const attendance = data?.task_attendance?.value || []
				addedUsers = [...addedUsers, ...attendance]

				// remove duplicate
				addedUsers = addedUsers.filter((a, i, c) => c.findIndex(cf => cf.task_id == a.task_id)  === i)
			}})
		}

		// add the user to the meeting
		emit('update:meeting', {
			...props.meeting,
			task_attendance: addedUsers
		})

		return; //prevent proceeding to add individual user
	} 

	MeetingApis.addAttendance({params, callback : (data) => {
		const attendance = data?.task_attendance?.value || []

		emit('update:meeting', {
			...props.meeting,
			task_attendance: attendance
		})
	}})
	

}

/**
 * Remove user from the list
 * @param {Object} attendee
 */
const removeUserFromList = (attendee) => {
	// remove user from the meeting
	const params = {
		meeting_id : props.meeting?.id,
		user_id : attendee.task_id,
		type : 'individual'
	}

	MeetingApis.removeUserFromAttendance({...params, callback : () => {
		const attendance = props.meeting?.task_attendance?.filter(a => a.task_id != attendee.task_id)
		emit('update:meeting', {
			...props.meeting,
			task_attendance: attendance
		})
	}})
}


/**
 * Compute group attendance percentage
 * @param {Object} group
 */
const groupAttendancePercent = (group) => {
	const total = group.users.length
	const completed = group.users.filter(a => a.is_completed).length
	const percent =  total ? Math.round((completed / total) * 100) : 0

	return `${percent}%`
}



/* ============= ON MOUNT ========== */
onBeforeMount(() => {
	
})




</script>